/**
 * Resolve bindings genéricos de um LinkAutoSource.
 *
 * Ausente: undefined, string vazia após trim, resolver sem resultado.
 * Presente deliberado: 0, false, null estático.
 *
 * Atributos: preferem o elemento matched (selector); fallback no anchor.
 */

import type {
  LinkValueBinding,
  LinkValueResolver,
  LinkValueTransform,
} from '@/config/shared/auto-source';

import { normalizePathname } from './LinkTargetMatcher';

export type LinkResolvedValue = string | number | boolean | null;

export type LinkResolvedBindings = Readonly<Record<string, LinkResolvedValue>>;

export type LinkResolveContext = {
  readonly anchor: HTMLAnchorElement;
  /** Elemento que casou o selector (ou o próprio anchor). */
  readonly target: Element;
  readonly url: URL;
  readonly pathParams: Readonly<Record<string, string>>;
};

export type LinkBindingsResolution = {
  readonly valid: boolean;
  readonly values: LinkResolvedBindings;
  readonly missingRequired: readonly string[];
};

function isAbsent(value: LinkResolvedValue | undefined): boolean {
  if (value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
}

function pathSegments(pathname: string): string[] {
  return normalizePathname(pathname)
    .split('/')
    .filter(Boolean)
    .map(seg => {
      try {
        return decodeURIComponent(seg);
      } catch {
        return seg;
      }
    });
}

/** Remove extensão estática comum de simulações HTML locais. */
function stripStaticExtension(segment: string): string {
  return segment.replace(/\.html?$/i, '');
}

function normalizeText(raw: string | null): string | undefined {
  if (raw == null) return undefined;
  const collapsed = raw.replace(/\s+/g, ' ').trim();
  return collapsed === '' ? undefined : collapsed;
}

function readDataset(el: Element, key: string): string | undefined {
  if (!(el instanceof HTMLElement)) return undefined;
  const value = el.dataset[key];
  if (value === undefined) return undefined;
  return value.trim() === '' ? undefined : value;
}

function readAttribute(el: Element, name: string): string | undefined {
  const value = el.getAttribute(name);
  if (value === null) return undefined;
  return value.trim() === '' ? undefined : value;
}

function resolveOne(
  resolver: LinkValueResolver,
  ctx: LinkResolveContext
): LinkResolvedValue | undefined {
  switch (resolver.source) {
    case 'query': {
      const value = ctx.url.searchParams.get(resolver.key);
      return value === null ? undefined : value;
    }
    case 'pathParam': {
      const value = ctx.pathParams[resolver.key];
      return value === undefined ? undefined : value;
    }
    case 'pathSegment': {
      const segments = pathSegments(ctx.url.pathname);
      let index = resolver.index;
      if (!Number.isInteger(index)) {
        return undefined;
      }
      if (index < 0) {
        index = segments.length + index;
      }
      if (index < 0 || index >= segments.length) {
        return undefined;
      }
      const raw = segments[index];
      return raw === undefined ? undefined : stripStaticExtension(raw);
    }
    case 'attribute': {
      return (
        readAttribute(ctx.target, resolver.name) ??
        readAttribute(ctx.anchor, resolver.name)
      );
    }
    case 'dataset': {
      return (
        readDataset(ctx.target, resolver.key) ??
        readDataset(ctx.anchor, resolver.key)
      );
    }
    case 'static':
      return resolver.value;
    case 'href':
      return ctx.url.href;
    case 'origin':
      return ctx.url.origin;
    case 'hostname':
      return ctx.url.hostname;
    case 'pathname':
      return normalizePathname(ctx.url.pathname);
    case 'text':
      return normalizeText(ctx.anchor.textContent);
    default:
      return undefined;
  }
}

function applyTransform(
  value: LinkResolvedValue,
  transform: LinkValueTransform
): LinkResolvedValue | undefined {
  switch (transform) {
    case 'trim':
      return typeof value === 'string' ? value.trim() : undefined;
    case 'lowercase':
      return typeof value === 'string' ? value.toLowerCase() : undefined;
    case 'uppercase':
      return typeof value === 'string' ? value.toUpperCase() : undefined;
    case 'number': {
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '' || !/^-?\d+(\.\d+)?$/.test(trimmed)) {
          return undefined;
        }
        const num = Number(trimmed);
        return Number.isFinite(num) ? num : undefined;
      }
      return undefined;
    }
    default:
      return undefined;
  }
}

function resolveBinding(
  binding: LinkValueBinding,
  ctx: LinkResolveContext
): { value: LinkResolvedValue | undefined; transformFailed: boolean } {
  const resolvers = Array.isArray(binding.from) ? binding.from : [binding.from];
  let sawTransformFailure = false;

  for (const resolver of resolvers) {
    const candidate = resolveOne(resolver, ctx);
    if (isAbsent(candidate)) continue;

    if (!binding.transform) {
      return { value: candidate, transformFailed: false };
    }

    const transformed = applyTransform(candidate!, binding.transform);
    if (transformed === undefined) {
      sawTransformFailure = true;
      continue;
    }
    if (typeof transformed === 'string' && transformed.trim() === '') {
      continue;
    }
    return { value: transformed, transformFailed: false };
  }

  return {
    value: undefined,
    transformFailed: sawTransformFailure,
  };
}

/**
 * Resolve todos os bindings. Não muta inputs.
 */
export function resolveLinkBindings(
  bindings: Readonly<Record<string, LinkValueBinding>> | undefined,
  ctx: LinkResolveContext
): LinkBindingsResolution {
  if (!bindings || Object.keys(bindings).length === 0) {
    return {
      valid: true,
      values: Object.freeze({}),
      missingRequired: Object.freeze([]),
    };
  }

  const values: Record<string, LinkResolvedValue> = Object.create(null);
  const missingRequired: string[] = [];

  for (const [key, binding] of Object.entries(bindings)) {
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      continue;
    }
    const { value } = resolveBinding(binding, ctx);
    if (value !== undefined) {
      values[key] = value;
      continue;
    }
    // Transform inválido só invalida se o binding for obrigatório.
    if (binding.required === true) {
      missingRequired.push(key);
    }
  }

  return {
    valid: missingRequired.length === 0,
    values: Object.freeze(values),
    missingRequired: Object.freeze(missingRequired),
  };
}
